import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import {
  createEvent, updateEvent,
  type EventStatus, type OpenHouseEvent, type OpenHouseEventInput,
} from "@/lib/openHouse";
import {
  ADMIN_STATUS_OPTIONS, adminStatusLabel, buildOpenHouseSlug, DEFAULT_OPEN_HOUSE_CONTACT_EMAIL,
} from "@/lib/openHouseSchedule";

type FormState = {
  title: string; event_date: string; start_time: string; end_time: string; end_time_approximate: boolean;
  location_name: string; address_line_1: string; city: string; state: string; postal_code: string;
  what_to_expect: string; status: EventStatus; status_note: string; contact_email: string;
  capacity_total: string; public_visible: boolean;
};

function initialState(event: OpenHouseEvent | null): FormState {
  if (!event) {
    return {
      title: "Silver Sage Open House", event_date: "", start_time: "18:00", end_time: "19:00",
      end_time_approximate: false, location_name: "", address_line_1: "", city: "", state: "NV",
      postal_code: "", what_to_expect: "", status: "registration_open", status_note: "",
      contact_email: DEFAULT_OPEN_HOUSE_CONTACT_EMAIL, capacity_total: "", public_visible: false,
    };
  }
  return {
    title: event.title, event_date: event.event_date,
    start_time: (event.start_time ?? "").slice(0, 5), end_time: (event.end_time ?? "").slice(0, 5),
    end_time_approximate: !!event.end_time_approximate,
    location_name: event.location_name ?? "", address_line_1: event.address_line_1 ?? "",
    city: event.city ?? "", state: event.state ?? "", postal_code: event.postal_code ?? "",
    what_to_expect: event.what_to_expect ?? "", status: event.status, status_note: event.status_note ?? "",
    contact_email: event.contact_email ?? DEFAULT_OPEN_HOUSE_CONTACT_EMAIL,
    capacity_total: event.capacity_total != null ? String(event.capacity_total) : "",
    public_visible: event.public_visible,
  };
}

const orNull = (s: string) => (s.trim() ? s.trim() : null);

export function OpenHouseEventDialog({ open, onOpenChange, event, onSaved }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null = create a new open house */
  event: OpenHouseEvent | null;
  onSaved: (id: string) => void;
}) {
  const [f, setF] = useState<FormState>(() => initialState(event));
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(initialState(event)); }, [open, event]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF((p) => ({ ...p, [k]: v }));

  const statusOptions: { value: string; label: string }[] = [...ADMIN_STATUS_OPTIONS];
  if (!statusOptions.some((o) => o.value === f.status)) {
    statusOptions.push({ value: f.status, label: `${adminStatusLabel(f.status)} (current)` });
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const problems: string[] = [];
    if (!f.title.trim()) problems.push("a title");
    if (!f.event_date) problems.push("a date");
    if (!f.start_time) problems.push("a start time");
    if (f.end_time && f.start_time && f.end_time <= f.start_time) problems.push("an end time after the start time");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.contact_email.trim())) problems.push("a valid contact email");
    const cap = f.capacity_total.trim() ? Number(f.capacity_total) : null;
    if (cap !== null && (!Number.isInteger(cap) || cap < 1)) problems.push("a whole-number seat capacity (or leave it blank)");
    if (problems.length) {
      toast({ title: "Please add " + problems.join(", "), variant: "destructive" });
      return;
    }
    const input: OpenHouseEventInput = {
      title: f.title.trim(),
      event_date: f.event_date,
      start_time: `${f.start_time}:00`,
      end_time: f.end_time ? `${f.end_time}:00` : null,
      end_time_approximate: !!f.end_time && f.end_time_approximate,
      location_name: orNull(f.location_name),
      address_line_1: orNull(f.address_line_1),
      city: orNull(f.city),
      state: orNull(f.state),
      postal_code: orNull(f.postal_code),
      what_to_expect: orNull(f.what_to_expect),
      status: f.status,
      status_note: orNull(f.status_note),
      contact_email: f.contact_email.trim(),
      capacity_total: cap,
      public_visible: f.public_visible,
    };
    setSaving(true);
    try {
      if (event) {
        await updateEvent(event.id, input);
        toast({ title: "Open House updated" });
        onSaved(event.id);
      } else {
        const suffix = Math.random().toString(36).slice(2, 6);
        const created = await createEvent(input, buildOpenHouseSlug(input.title, input.event_date, suffix));
        toast({ title: "Open House added" });
        onSaved(created.id);
      }
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: "Couldn't save the Open House", description: err?.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const showsNote = f.status === "postponed" || f.status === "cancelled";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{event ? "Edit Open House" : "Add Open House"}</DialogTitle>
          <DialogDescription>
            Only published open houses appear on the public Open House page. The next upcoming one is featured.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <F label="Title" required><Input value={f.title} onChange={(e) => set("title", e.target.value)} maxLength={150} /></F>

          <div className="grid sm:grid-cols-3 gap-3">
            <F label="Date" required><Input type="date" value={f.event_date} onChange={(e) => set("event_date", e.target.value)} /></F>
            <F label="Start time" required><Input type="time" value={f.start_time} onChange={(e) => set("start_time", e.target.value)} /></F>
            <F label="End time"><Input type="time" value={f.end_time} onChange={(e) => set("end_time", e.target.value)} /></F>
          </div>
          {f.end_time && (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={f.end_time_approximate} onCheckedChange={(v) => set("end_time_approximate", !!v)} />
              End time is approximate (shows "to approximately …")
            </label>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Location name"><Input value={f.location_name} onChange={(e) => set("location_name", e.target.value)} placeholder="e.g. Grace Community Church (basement)" maxLength={200} /></F>
            <F label="Street address"><Input value={f.address_line_1} onChange={(e) => set("address_line_1", e.target.value)} maxLength={200} /></F>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <F label="City"><Input value={f.city} onChange={(e) => set("city", e.target.value)} maxLength={100} /></F>
            <F label="State"><Input value={f.state} onChange={(e) => set("state", e.target.value)} maxLength={30} /></F>
            <F label="ZIP"><Input value={f.postal_code} onChange={(e) => set("postal_code", e.target.value)} maxLength={20} /></F>
          </div>

          <F label="Description"><Textarea rows={4} value={f.what_to_expect} onChange={(e) => set("what_to_expect", e.target.value)} maxLength={3000} /></F>

          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Status">
              <Select value={f.status} onValueChange={(v) => set("status", v as EventStatus)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {statusOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Seat capacity (optional)">
              <Input type="number" min={1} step={1} value={f.capacity_total} onChange={(e) => set("capacity_total", e.target.value)} placeholder="No limit" />
            </F>
          </div>

          <F label={showsNote ? "Announcement shown on the public page" : "Announcement (shown only when Postponed or Cancelled)"}>
            <Textarea rows={3} value={f.status_note} onChange={(e) => set("status_note", e.target.value)} maxLength={2000}
              placeholder="e.g. This Open House will not take place as scheduled. We'll post a new date soon." />
          </F>

          <div className="grid sm:grid-cols-2 gap-3 items-end">
            <F label="Contact email" required><Input type="email" value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)} maxLength={200} /></F>
            <label className="flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm">
              <span>
                <span className="font-medium block">Published</span>
                <span className="text-xs text-muted-foreground">Visible on the public site</span>
              </span>
              <Switch checked={f.public_visible} onCheckedChange={(v) => set("public_visible", v)} />
            </label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {event ? "Save changes" : "Add Open House"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium leading-none">{label}{required && <span className="text-destructive"> *</span>}</span>
      {children}
    </label>
  );
}
